import { z } from "zod";

import { accessionCaseTypes } from "../models/accession-case-type";

function nullString() {
  return z
    .string()
    .nullish()
    .transform((str) => str || null);
}

export const filters = z.object({
  myAccessions: z.boolean().prefault(false),
  status: z
    .enum([
      "new",
      "received",
      "in_progress",
      "completed",
      "reported",
      "final",
      "cancelled",
    ])
    .array()
    .prefault([]),
  holdStatus: z.enum(["active", "resolved", "canceled"]).array().prefault([]),
  client: z.number().array().prefault([]),
  sharedClient: z.number().array().prefault([]),
  billToOrg: z.number().array().prefault([]),
  splitBillingOrgs: z.number().array().prefault([]),
  test: z.number().array().prefault([]),
  panel: z.number().array().prefault([]),
  testType: z
    .enum(["SEROLOGY", "CLINICAL", "HLA", "MICROBIOLOGY"])
    .array()
    .prefault([]),
  priority: z.enum(["STAT", "ROUTINE"]).array().prefault([]),
  accessionIds: z.number().array().prefault([]),
  labs: z.number().array().prefault([]),
  hasSendOut: z.boolean().prefault(false),
});

export const listInput = z.object({
  // predefinedFilter: z
  //   .enum(["recent", "pending", "completed", "cancelled", "holds"])
  //   .default("recent"),
  dateRange: z.date().optional(),
  take: z.number().prefault(50),
  filter: filters,
});

export type AccessionListFilter = z.infer<typeof filters>;
export type AccessionListInput = z.infer<typeof listInput>;

export const instructionSchemaForm = z.union([
  z.object({
    type: z.literal("test"),
    testId: z.number(),
  }),
  z.object({
    type: z.literal("panel"),
    panelId: z.number(),
  }),
]);

export const customSampleSchema = z
  .object({
    tubeId: z.number(),
    collection: z.string().nullish(),
    collectionTimezone: z.string().nullish(),
    refrigerated: z.string().nullish(),
    refrigeratedTimezone: z.string().nullish(),
    centrifuged: z.string().nullish(),
    centrifugedTimezone: z.string().nullish(),
    quantity: z.number().optional(),
    lot: z.string().nullish(),
    expiration: z.string().nullish(),
    typeId: z.string().nullish(),
    transfusionId: z.string().nullish(),
    timeZone: z.string().nullish(),
  })
  .catchall(
    z.object({
      fieldSpecId: z.number(),
      textValue: z.string().nullish(),
      numericValue: z.coerce.number<number>().nullish(),
      dateValue: z.string().nullish(),
      booleanValue: z.boolean().nullish(),
      optionId: z.number().nullish(),
    }),
  );

export const newSampleSchema = customSampleSchema;

export const createNew = z.object({
  subjectId: z.coerce
    .number<number>({
      error: (issue) =>
        issue.input === undefined ? undefined : "A donor is required",
    })
    .min(1, "Required"),
  caseType: z.enum(accessionCaseTypes).nullish(),
  priority: z.enum(["ROUTINE", "STAT"]).prefault("ROUTINE"),

  receivingOrgId: z.coerce
    .number<number>({
      error: (issue) =>
        issue.input === undefined ? undefined : "Receiving org is required",
    })
    .min(1, "Required"),
  orgId: z.coerce
    .number<number>({
      error: (issue) =>
        issue.input === undefined ? undefined : "A primary client is required",
    })
    .min(1, "Required"),
  billToOrgId: z.coerce.number<number>().optional(),
  sharedOrgs: z
    .array(z.coerce.number<number>().min(1, "Required"))
    .prefault([]),
  additionalEmails: z.array(z.email().min(1, "Required")).prefault([]),
  splitBillingOrgs: z
    .array(z.coerce.number<number>().min(1, "Required"))
    .prefault([]),

  instructions: z
    .array(instructionSchemaForm)
    .min(1, "At least one test order is required")
    .prefault([]),

  samples: z
    .array(newSampleSchema)
    .min(1, "At least one sample is required")
    .prefault([]),

  collectTimezone: z.string().min(1, "Required"),
  packedAt: z.string().nullish(),
  packedTimezone: z.string().nullish(),
  trackingId: z.string().optional(),
  requisitionId: z.string().optional(),

  createAnother: z.boolean().prefault(false),
});

export const editShipping = z.object({
  accessionId: z.number(),
  caseType: z.enum(accessionCaseTypes).nullish(),
  receivingOrgId: z.coerce.number<number>().min(1, "Required"),
  packedAt: z.string().nullish(),
  packedTimezone: z.string().nullish(),
  trackingId: z.string().nullish(),
  requisitionId: z.string().nullish(),
  // When accession is received, service requires a Date (null/omitted rejected).
  // See docs/engineering/receive-rules.md and AccessionService.updateShippingDetails.
  receivedAt: z.coerce.date().nullish(),
});
